import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, Icon, Insight, SubHeading, Table, Text} from '@forecast-it/design-system';
import {graphql, useFragment} from 'react-relay';
import {EmptyState} from './EmptyState';
import {ScrollableTable} from './ScrollableTable';
import type {LinkedTasksTab_ticket$key} from './__generated__/LinkedTasksTab_ticket.graphql';
import {useUnlinkForecastTaskFromTicket} from '../hooks/useUnlinkForecastTaskFromTicket';
import {useAuth} from '../../auth/context/AuthContext';
import {hasTicketPerm, TICKET_PERMS} from '../../auth/utils/ticketPerms';
import {LogTimeModal} from './LogTimeModal';
import {useOpenNativeTaskModal} from '../hooks/useOpenNativeTaskModal';

const LinkedTasksTabFragment = graphql`
	fragment LinkedTasksTab_ticket on Ticket {
		id
		aiSuggestedLinkedTaskIds
		totalLinkedTaskMinutes
		linkedTasks {
			id
			companyTaskId
			name
			totalMinutesRegistered
			estimateForecastMinutes
			deadlineYear
			deadlineMonth
			deadlineDay
			statusColumnV2 {
				name
			}
			project {
				id
				name
			}
			assignedPersons {
				id
				fullName
			}
		}
	}
`;

interface Props {
	ticket: LinkedTasksTab_ticket$key;
	onAddTask?: () => void;
	currentPersonId: string;
	/** Called after time is logged so the ticket-level minute rollup refreshes. */
	onRefetch?: () => void;
}

/** Hours to one decimal, trailing zero dropped: `1.5`, `2`, `0`. */
function toHours(minutes: number): number {
	return Number((minutes / 60).toFixed(2));
}

/** A deadline as `12 Mar 2026`, or an em-dash when the task has none. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDeadline(year: number | null, month: number | null, day: number | null): string {
	if (year == null || month == null || day == null) return '—';
	return `${day} ${MONTHS[month - 1] ?? ''} ${year}`.replace(/\s+/g, ' ').trim();
}

const Stack = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[500]};
`;
const Header = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[400]};
`;
const Intro = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const EmptyBody = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;

interface TaskRow {
	id: string;
	taskId: string;
	name: string;
	status: string;
	owner: string;
	deadline: string;
	logged: number;
	estimate: number;
	progress: number;
	companyTaskId: number | null;
	projectId: string | null;
	fromAi: boolean;
}

/**
 * The Forecast tasks doing this ticket's work, and what they have cost.
 *
 * The rollup sits above the table because the totals are the reason to come here: an
 * agent wants to know whether the work is bigger than the estimate before reading any
 * one task. The design system's `Insight` lays its sections out in two rows by index
 * parity, so the order passed in reads down-then-across, not left to right.
 *
 * A task's own detail belongs to the platform's task modal, which the name opens —
 * this view stays a summary and a way in.
 */
export function LinkedTasksTab({ticket: ticketRef, onAddTask, currentPersonId, onRefetch}: Props) {
	const data = useFragment(LinkedTasksTabFragment, ticketRef);
	const openTaskDetail = useOpenNativeTaskModal();
	const {user} = useAuth();
	const {submit: unlinkTask, loading: unlinking} = useUnlinkForecastTaskFromTicket();
	const canManage = !!user && hasTicketPerm(user.ticketPerms, TICKET_PERMS.MANAGE);
	const [logTask, setLogTask] = useState<{id: string; name: string; projectId: string | null} | null>(null);

	const aiSuggestedIds = useMemo(() => new Set(data.aiSuggestedLinkedTaskIds), [data.aiSuggestedLinkedTaskIds]);

	const rows: TaskRow[] = useMemo(
		() =>
			data.linkedTasks.map(task => {
				const logged = toHours(task.totalMinutesRegistered ?? 0);
				const estimate = toHours(task.estimateForecastMinutes ?? 0);
				return {
					id: task.id,
					taskId: task.companyTaskId != null ? `T${task.companyTaskId}` : '—',
					name: task.name ?? 'Untitled task',
					status: task.statusColumnV2?.name ?? '—',
					owner: task.assignedPersons?.map(p => p?.fullName).filter(Boolean)[0] ?? 'Unassigned',
					deadline: formatDeadline(task.deadlineYear ?? null, task.deadlineMonth ?? null, task.deadlineDay ?? null),
					logged,
					estimate,
					// A task with no estimate has no progress to report — 0 rather than a
					// division by zero that would read as 100%.
					progress: estimate > 0 ? Math.min(1, logged / estimate) : 0,
					companyTaskId: task.companyTaskId ?? null,
					projectId: task.project?.id ?? null,
					fromAi: aiSuggestedIds.has(task.id),
				};
			}),
		[data.linkedTasks, aiSuggestedIds]
	);

	const totals = useMemo(() => {
		const estimate = rows.reduce((sum, row) => sum + row.estimate, 0);
		const logged = toHours(data.totalLinkedTaskMinutes);
		return {
			count: rows.length,
			estimate: Number(estimate.toFixed(2)),
			logged,
			remaining: Number(Math.max(0, estimate - logged).toFixed(2)),
		};
	}, [rows, data.totalLinkedTaskMinutes]);

	const handleUnlink = (forecastTaskId: string) => {
		unlinkTask({ticketId: data.id, forecastTaskId}).catch(() => {});
	};

	// The action column reads `props.hidden` off every child, so a conditional `null`
	// among them throws — build the list and filter before passing it.
	const rowActions = [
		<Table.ActionColumn.Option
			key="log"
			onClick={(row: TaskRow) => setLogTask({id: row.id, name: row.name, projectId: row.projectId})}
		>
			Log time
		</Table.ActionColumn.Option>,
		canManage ? (
			<Table.ActionColumn.Option key="unlink" disabled={unlinking} onClick={(row: TaskRow) => handleUnlink(row.id)}>
				Unlink task
			</Table.ActionColumn.Option>
		) : null,
	].filter(Boolean);

	return (
		<Stack data-testid="tasks-tab">
			<Header>
				<Intro>
					<SubHeading data-cy="tasks-heading">Tasks</SubHeading>
					<Detail color="medium">Time is logged against tasks. The totals below roll up to this ticket.</Detail>
				</Intro>
				{onAddTask && (
					<Button emphasis={EMPHASIS.HIGH} icon="plus" onPress={onAddTask} data-cy="add-task">
						New task
					</Button>
				)}
			</Header>

			{/* Passed down-then-across: Insight splits its sections across two rows by
			    index parity, so this order renders as Tasks / Logged over Estimate /
			    Remaining. */}
			<Insight height="m" data-cy="tasks-rollup">
				<Insight.Section title="Tasks" value={String(totals.count)} size="l" data-cy="tasks-count" />
				<Insight.Section title="Total estimate" value={`${totals.estimate}h`} size="l" data-cy="tasks-estimate" />
				<Insight.Section title="Total logged" value={`${totals.logged}h`} size="l" data-cy="tasks-logged" />
				<Insight.Section title="Remaining" value={`${totals.remaining}h`} size="l" data-cy="tasks-remaining" />
			</Insight>

			{rows.length === 0 ? (
				<EmptyState data-testid="tasks-empty-state">
					<EmptyBody>
						<Text color="auxiliary">No tasks yet. Add one to start logging time.</Text>
						{onAddTask && (
							<Button emphasis={EMPHASIS.MEDIUM} icon="plus" onPress={onAddTask} data-cy="add-task-empty">
								New task
							</Button>
						)}
					</EmptyBody>
				</EmptyState>
			) : (
				<ScrollableTable data-testid="tasks-table-scroll">
					<Table data={rows} getRowId={row => row.id} width="100%" data-cy="tasks-table">
						<Table.TextColumn accessorKey="taskId" header="Id" width="90px" />
						<Table.TextColumn
							accessorKey="name"
							header="Task"
							ellipsis
							icon={(row: TaskRow) => (row.fromAi ? <Icon icon="sparkle" size="s" /> : undefined)}
							onClick={(row: TaskRow) =>
								row.companyTaskId != null ? () => openTaskDetail(row.companyTaskId as number) : undefined
							}
						/>
						<Table.TextColumn accessorKey="status" header="Status" width="150px" />
						<Table.AvatarColumn accessorKey="owner" header="Owner" width="190px" />
						{/* Deadline needs enough room for the "12 Mar 2026" reading. Squeezing it,
						    which is what happened before, cut the year off mid-digit. */}
						<Table.TextColumn accessorKey="deadline" header="Deadline" width="140px" />
						<Table.NumberColumn accessorKey="logged" header="Logged (h)" width="120px" />
						<Table.NumberColumn accessorKey="estimate" header="Estimate (h)" width="130px" />
						{/* Progress is the "34%" reading — a percentage plus the design system's
						    thin progress bar. It needs headroom for the header word and the bar. */}
						<Table.PercentageColumn accessorKey="progress" header="Progress" width="140px" />
						<Table.ActionColumn header="Actions" hideHeader>
							{rowActions}
						</Table.ActionColumn>
					</Table>
				</ScrollableTable>
			)}

			{logTask && (
				<LogTimeModal
					open
					taskId={logTask.id}
					projectId={logTask.projectId}
					taskName={logTask.name}
					personId={currentPersonId}
					ticketId={data.id}
					onClose={() => setLogTask(null)}
					onSuccess={() => {
						setLogTask(null);
						onRefetch?.();
					}}
				/>
			)}
		</Stack>
	);
}
